import type {
  RequestOptions,
  UploadImageResult,
  UploadVideoResult,
  XmaxOpenClient,
} from '../api/client'

export type FileClient = {
  /** Upload an image file and return its remote URL metadata. Accepts an optional abort signal. */
  uploadImage: (file: File, options?: RequestOptions) => Promise<UploadImageResult>
  /** Upload a video file and return its remote URL metadata. Accepts an optional abort signal. */
  uploadVideo: (file: File, options?: RequestOptions) => Promise<UploadVideoResult>
  /** Alias of `uploadImage()` retained by the file client. */
  upload: (file: File, options?: RequestOptions) => Promise<UploadImageResult>
  /**
   * Upload then run `/cos/image/check`.
   * Returns the upload metadata with the checked image URL.
   *
   * @example
   * ```ts
   * const result = await client.files.uploadAndCheckImage(file)
   * await session.set({
   *   prompt: session.context.prompt,
   *   refImageUrl: result.url,
   * })
   * ```
   */
  uploadAndCheckImage: (file: File, options?: RequestOptions) => Promise<UploadImageResult>
}

/** @internal Create the file capability exposed by {@link XmaxClient.files}. */
export function createFileClient(client: XmaxOpenClient): FileClient {
  return {
    uploadImage: (file: File, options?: RequestOptions) => options
      ? client.uploadImage(file, options)
      : client.uploadImage(file),
    uploadVideo: (file: File, options?: RequestOptions) => options
      ? client.uploadVideo(file, options)
      : client.uploadVideo(file),
    upload: (file: File, options?: RequestOptions) => options
      ? client.uploadImage(file, options)
      : client.uploadImage(file),
    uploadAndCheckImage: (file: File, options?: RequestOptions) => options
      ? client.uploadAndCheckImage(file, options)
      : client.uploadAndCheckImage(file),
  }
}
